// Libraries
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useSnackbar } from 'notistack';

// MUI
import { Container, Grid } from '@mui/material';

// Core
import { ResourceTextApplication } from '../../../../core/resources';
import log from '../../../../core/log';

// Common
import { ViewTitle } from '../../../../common/typography';
import ViewLoader from '../../../../common/ViewLoader';
import SearchCalendar from '../../../../common/search/SearchCalendar';

// Interfaces
import {
    IAnimalAnnouncement,
    IAnimalAnnouncementWithFarm,
    IUpdateAnnouncementResult,
} from '../../../../interfaces/IAnnouncement';
import { IFarmDetails } from '../../../../interfaces/IBusinessEntities';
import {
    AnimalFilterTypeEnum,
    AnnouncementFlagsEnum,
    AnnouncementTypeEnum,
    DialogModeEnum,
} from '../../../../interfaces/enums';
import { ITiltuApiResult } from '../../../../interfaces/IGeneral';
import { GUIDType } from '../../../../interfaces/types';
import {
    IAnimalSummaryExtraInfo,
    IFarmAnimal,
    IFilteredAnimalList,
} from '../../../../interfaces/IAnimal';

// Store
import { useAppSelector } from '../../../../store/hooks';

// API
import companyApi from '../../../../api/companyApi';
import animalAnnouncementApi from '../../../../api/animalAnnouncementApi';
import animalListApi from '../../../../api/animalListApi';

// Feature
import { AnimalAnnouncementsListViewStrings } from './strings/AnimalAnnouncementsListViewStrings';
import ControlButtons from './components/control/ControlButtons';
import AnimalAnnouncementListViewGrid from './components/grid/AnimalAnnouncementListViewGrid';

// Feature - Animal Announcements
import BovineSlaughterAnnouncement from '../bovine-slaughter-announcement/BovineSlaughterAnnouncement';
import BovineIntermediationAnnouncement from '../bovine-intermediation-announcement/BovineIntermediationAnnouncement';
import BovinePurchaseAnnouncement from '../bovine-purchase-announcement/BovinePurchaseAnnouncement';
import { mapAnnouncementResult } from '../bovine-slaughter-announcement/helpers/mapper';
import ManualAnnouncementDialog from '../../../manual-announcements/components/ManualAnnouncementDialog';
import { getAnimalGroup, isAnimalAnnouncementUpdateRestricted, mapAnimalAnnouncementAnimalsToIAnimalSummaryExtraInfo } from '../../helpers/data';
import { fetchCanCreateAnimalAnnouncements } from '../../../shared/validators/canCreateAnnouncementValidator';
import ConfirmBovinePurchaseAnnouncement from '../bovine-purchase-announcement/confirm/ConfirmBovinePurchaseAnnouncement';
import { isNotNil } from 'ramda';
import groupsApi from '../../../../api/groupsApi';
import { IMainGroup } from '../../../../interfaces/IGroup';
import utils from '../../../../core/utils';

interface IConfirmRelatedAnnouncement {
    announcement: IAnimalAnnouncement | null;
    openDialog: boolean;
}

// announcement search view
export default function AnimalAnnouncementsListView() {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral', 'AnelmaLayout']);
    const context = useAppSelector((state) => state.context);
    const { enqueueSnackbar } = useSnackbar();
    const dateFormat = 'YYYY-MM-DDTHH:mm:ss';

    const [viewLoaderTimer, setViewLoaderTimer] = useState<number>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [farmData, setFarmData] = useState<IFarmDetails | null>(null);
    const [displayAll, setDisplayAll] = useState<boolean>(false);
    const [animalAnnouncementData, setAnimalAnnouncementData] = useState<IAnimalAnnouncement[]>([]);
    const [userSelectedAnimalAnnouncementData, setUserSelectedAnimalAnnouncementData] = useState<
        IAnimalAnnouncement[]
    >([]);
    const [selectedAnimalAnnouncementType, setSelectedAnimalAnnouncementType] =
        useState<string>('');
    const [selectedAnimalAnnouncementStatus, setSelectedAnimalAnnouncementStatus] =
        useState<string>('');
    const [selectedAnimalAnnouncement, setSelectedAnimalAnnouncement] =
        useState<IAnimalAnnouncement | null>(null);
    const [animalAnnouncementDialogVisible, setAnimalAnnouncementDialogVisible] =
        useState<boolean>(false);
    const [fromDate, setFromDate] = useState<moment.Moment>(moment().add(-1, 'y'));
    const [toDate, setToDate] = useState<moment.Moment>(moment());
    const [canCreateAnimalAnnouncement, setCanCreateAnimalAnnouncement] = useState<boolean | null>(
        null,
    );
    const [farmAnimals, setFarmAnimals] = useState<IAnimalSummaryExtraInfo[] | null>(null);
    const [farmAnimalGroups, setFarmAnimalGroups] = useState<IMainGroup[] | null>(null);
    const [confirmBovinePurchaseAnnouncement, setConfirmBovinePurchaseAnnouncement] =
        useState<IConfirmRelatedAnnouncement>({ announcement: null, openDialog: false });

    const [reloadChainInfos, setReloadChainInfos] = useState<boolean>(false);

    useEffect(() => {
        setViewLoaderTimer(15);
    }, []);

    useEffect(() => {
        if (context?.data?.currentContext) {
            setIsLoading(true);
            companyApi
                .getCompanyByContext(context.data.currentContext?.context)
                .then((response) => {
                    if (response?.Entity) setFarmData(response.Entity as IFarmDetails);
                    else setFarmData(null);
                    setIsLoading(false);
                });
            setIsLoading(false);
        } else setFarmData(null);
    }, [context]);

    useEffect(() => {
        if (farmData && farmData.Id && !displayAll) {
            refreshAnnouncements();
            setSelectedAnimalAnnouncementType('1');
            setSelectedAnimalAnnouncementStatus('');
            fetchCanCreateAnimalAnnouncements(
                farmData.Id,
                setCanCreateAnimalAnnouncement,
                enqueueSnackbar
            );

            fetchFarmAnimals().then(() => {
                fetchFarmGroups(farmData.Id);
            });
        }
    }, [farmData]);

    useEffect(() => {
        if (farmAnimalGroups !== null && farmAnimals != null && farmAnimals.length > 0) {
            setFarmAnimals((previous) => previous === null ? null : countExternalFields(previous));
        }
    }, [farmAnimalGroups]);

    useEffect(() => {
        sortAnimalAnnouncementsBySelectedDates();
    }, [displayAll])

    useEffect(() => {
        setUserSelectedAnimalAnnouncementData(
            animalAnnouncementData.filter((animalAnnouncement) => {
                return filterAnimalAnnouncementsByUserInput(animalAnnouncement);
            }),
        );
    }, [animalAnnouncementData, selectedAnimalAnnouncementType, selectedAnimalAnnouncementStatus]);

    useEffect(() => {
        if (viewLoaderTimer === 0) setViewLoaderTimer(0);
        if (!viewLoaderTimer) return;
        const intervalId = setInterval(() => {
            setViewLoaderTimer(viewLoaderTimer - 1);
        }, 1000);
        return () => clearInterval(intervalId);
    }, [viewLoaderTimer]);

    const countExternalFields = (animals: IAnimalSummaryExtraInfo[]) => {
        if (animals !== null) {
            let res = [...animals];

            res.forEach((a, i) => {
                a.Eu4 =
                    a.EuIdentifier?.length > 0
                        ? a.EuIdentifier.substring(a.EuIdentifier.length - 6, a.EuIdentifier.length - 2)
                        : null;

                a.AgeMonths = utils.calculateAgeInMonths(moment(), moment(a.BirthDate), 30.33);
                a.Group = getAnimalGroup(a.Id, farmAnimalGroups);
            });

            return res;
        }

        return null;
    }

    const fetchFarmAnimals = async () => {
        if (farmData?.Id) {
            await animalListApi
                .getFilteredAnimals(farmData?.Id)
                .then((response) => {
                    if (response) {
                        setFarmAnimals(getAllAnimals(response.Items));
                    }
                })
                .catch(() => {
                    handleShowSnackbarGeneralError();
                });
        }
    };

    const fetchFarmGroups = async (farmId: string|null) => {
        if (farmId) {
            await groupsApi
                .getGroups(String(farmId))
                .then((response) => {
                    if (response?.Items) {
                        setFarmAnimalGroups(response.Items);
                    }
                })
                .catch((err) => {
                    log('error', `groupsApi.getGroups error: ${err}`);
                    return;
                });
        }
    };

    const refreshAnnouncements = async () => {
        if (farmData !== null) {
            setIsLoading(true);
            await animalAnnouncementApi
                .getAnimalAnnouncementsFromAnelma(String(farmData.Id))
                .then((response) => {
                    if (!response) {
                        log(
                            'info',
                            `getAnimalAnnouncementsFromAnelma response returned no results for farm with id: ${farmData.Id}.`,
                        );
                        setIsLoading(false);
                        return;
                    }
                    setAnimalAnnouncementData(response.Items);
                    setIsLoading(false);
                });
        }
    };

    const fetchAllAnimalAnnouncements = useCallback(
        async (range: string) => {
            setIsLoading(true);
            await animalAnnouncementApi
                .getAllAnimalAnnouncementsFromAnelma(range, true)
                .then((response) => {
                    if (!response) {
                        log(
                            'info',
                            `getAllAnimalAnnouncementsFromAnelma response returned no results.`,
                        );
                        setIsLoading(false);
                        return;
                    }
                    const filteredItems = response.Items.filter(
                        (item) => (item.Status === 'AL' ||item.Status === 'AV' || item.Status === 'OS')
                            && (item as IAnimalAnnouncementWithFarm).Farm,
                    );
                    setAnimalAnnouncementData(filteredItems.map(i => i as IAnimalAnnouncementWithFarm));
                    setIsLoading(false);
                });
        },
        [animalAnnouncementData, selectedAnimalAnnouncement],
    );

    const filterAnimalAnnouncementsByUserInput = (animalAnnouncement: IAnimalAnnouncement) => {
        if (
            (animalAnnouncement.Type === AnnouncementTypeEnum.Slaughter ||
                animalAnnouncement.Type === AnnouncementTypeEnum.Intermediation ||
                animalAnnouncement.Type === AnnouncementTypeEnum.ManualBovineSlaughter ||
                animalAnnouncement.Type === AnnouncementTypeEnum.ManualBovineIntermediation ||
                animalAnnouncement.Type === AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement ||
                animalAnnouncement.Type === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
            ) && Number(selectedAnimalAnnouncementType) === 1
        )
            return filterAnimalAnnouncementByUserRefinedDates(animalAnnouncement, fromDate, toDate)
                && filterAnimalAnnouncementByUserRefinedStatus(animalAnnouncement);
        else if (
            (animalAnnouncement.Type === AnnouncementTypeEnum.BovinePurchase ||
                animalAnnouncement.Type === AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
            ) && Number(selectedAnimalAnnouncementType) === 2
        )
            return filterAnimalAnnouncementByUserRefinedDates(animalAnnouncement, fromDate, toDate)
                && filterAnimalAnnouncementByUserRefinedStatus(animalAnnouncement);
    };

    const filterAnimalAnnouncementByUserRefinedDates = (
        animalAnnouncement: IAnimalAnnouncement,
        fromDate: moment.Moment,
        toDate: moment.Moment,
    ) => {
        if (
            moment(`${animalAnnouncement.Created}Z`).format(dateFormat) >=
            fromDate.startOf('day').format(dateFormat) &&
            moment(`${animalAnnouncement.Created}Z`).format(dateFormat) <=
            toDate.endOf('day').format(dateFormat)
        ) {
            return animalAnnouncement;
        }
    };

    const filterAnimalAnnouncementByUserRefinedStatus = (
        animalAnnouncement: IAnimalAnnouncement
    ) => {
        return selectedAnimalAnnouncementStatus === ''
            || animalAnnouncement.Status === selectedAnimalAnnouncementStatus;
    };

    const sortAnimalAnnouncementsBySelectedDates = () => {
        if (displayAll) {
            const oneWeekAgo = moment().add(-1, 'week').format(dateFormat);
            fetchAllAnimalAnnouncements(oneWeekAgo);
            return;
        } else if (!displayAll) {
            refreshAnnouncements();
        }
        setUserSelectedAnimalAnnouncementData(
            animalAnnouncementData.filter((animalAnnouncement) => {
                return filterAnimalAnnouncementsByUserInput(animalAnnouncement);
            }),
        );
    };

    const removeAnimalAnnouncement = (announcementNumber: number) => {
        setAnimalAnnouncementData(
            animalAnnouncementData.filter((_) => _.Number !== announcementNumber),
        );
        setUserSelectedAnimalAnnouncementData(
            userSelectedAnimalAnnouncementData.filter((_) => _.Number !== announcementNumber),
        );
    };

    const removeAnimalAnnouncementById = (announcementId: GUIDType) => {
        setAnimalAnnouncementData(animalAnnouncementData.filter((_) => _.Id !== announcementId));
        setUserSelectedAnimalAnnouncementData(
            userSelectedAnimalAnnouncementData.filter((_) => _.Id !== announcementId),
        );
    };

    const openConfirmBovinePurchaceAnnouncement = (id?: GUIDType) => {
        if (id) {
            animalAnnouncementApi
                .getRelatedAnnouncement(id)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaGeneral:1020'), {
                            variant: 'error',
                        });
                        return;
                    }

                    if (response.Entity)
                        setConfirmBovinePurchaseAnnouncement({
                            announcement: response.Entity,
                            openDialog: true,
                        });
                });
        }
    };

    const handleAnimalAnnouncementDialogVisible = async (data: IAnimalAnnouncement) => {
        if (displayAll) {
            let farm = (data as IAnimalAnnouncementWithFarm).Farm;

            if (farm)
                await fetchFarmGroups(farm.Id);
        }

        setSelectedAnimalAnnouncement(data);
        setAnimalAnnouncementDialogVisible(true);
    };

    const handleAnimalAnnouncementDialogClosed = (openPurchaseForConfirmation?: boolean) => {
        let announcementId = selectedAnimalAnnouncement?.Id;
        setAnimalAnnouncementDialogVisible(false);
        setSelectedAnimalAnnouncement(null);

        if (openPurchaseForConfirmation)
            openConfirmBovinePurchaceAnnouncement(announcementId);
    };

    const sortAnimalAnnouncementsOnUpdate = (
        updatedAnimalAnnouncement: IAnimalAnnouncement,
        data: IAnimalAnnouncement[],
    ) => {
        if (updatedAnimalAnnouncement.Number)
            return data.filter(
                (_) =>
                    _.Number !==
                    [...data].find((data) => {
                        return data.Number === updatedAnimalAnnouncement.Number;
                    })?.Number
            );
        else
            return data.filter(
                (_) =>
                    _.Id !==
                    [...data].find((data) => {
                        return data.Id === updatedAnimalAnnouncement.Id;
                    })?.Id
            );
    };


    const handleAnnouncementReload = (id: GUIDType) => {
        if (farmData && farmData.Id)
            animalAnnouncementApi.getAnimalAnnouncementsFromAnelma(farmData.Id).then((res) => {
                for (let item of res?.Items || []) {
                    if (item.Id === id) {
                        const index = animalAnnouncementData.findIndex((x) => x.Id === id);
                        if (index !== -1) {
                            let copy = [...animalAnnouncementData];
                            copy[index] = item;
                            setAnimalAnnouncementData(copy);
                        } else {
                            setAnimalAnnouncementData([item, ...animalAnnouncementData]);
                        }
                        return;
                    }
                }
            });
    }

    const updateAnnouncementState = (updatedAnimalAnnouncement: IAnimalAnnouncement) => {
        const animalAnnouncementBeforeUpdate = animalAnnouncementData.filter(
            (_) =>
                _.Id ===
                [...animalAnnouncementData].find((data) => {
                    return data.Id === updatedAnimalAnnouncement.Id;
                })?.Id
        );
        // [AN-1279] Preserve some of the current field values. Made because of Created-value is not returned from API/Tiltu.
        const updatedObject = animalAnnouncementBeforeUpdate.map((_) => {
            if (_.Id === updatedAnimalAnnouncement.Id)
                return {
                    Id: _.Id, // Does not change
                    Number: _.Number, // Does not change
                    Type: updatedAnimalAnnouncement.Type,
                    Created: _.Created, // Cannot change
                    ScheduledFrom: _.ScheduledFrom, // Cannot change
                    HoldingSiteId: updatedAnimalAnnouncement.HoldingSiteId,
                    Info: updatedAnimalAnnouncement.Info,
                    Status: updatedAnimalAnnouncement.Status,
                    Batches: updatedAnimalAnnouncement.Batches,
                    HasFiles: updatedAnimalAnnouncement.HasFiles,
                    Flags: updatedAnimalAnnouncement.Flags,
                };
        });
        const updatedAnimalAnnouncementData = sortAnimalAnnouncementsOnUpdate(
            updatedObject[0] as IAnimalAnnouncement,
            animalAnnouncementData,
        );
        if (
            updatedObject[0] &&
            isNotNil(updatedObject[0]?.Flags) &&
            updatedObject[0].Flags === AnnouncementFlagsEnum.Deleted
        ) {
            setAnimalAnnouncementData(updatedAnimalAnnouncementData);
        } else {
            updatedAnimalAnnouncementData.push(updatedObject[0] as IAnimalAnnouncement);
            setAnimalAnnouncementData(updatedAnimalAnnouncementData);
        }
    };

    // This is used when modifications are made to the announcement
    const handleAnnouncementUpdate = (
        data: IUpdateAnnouncementResult,
        type: AnnouncementTypeEnum,
        holdingSiteId: string,
    ) => {

        if (type !== AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
            && type !== AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
            && type !== AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
        ) {
            // announcement saving can affect other announcements
            // so we want to refresh the whole list
            sortAnimalAnnouncementsBySelectedDates();
        }
        else
            updateAnnouncementState(mapAnnouncementResult(data, holdingSiteId, type));
    };

    const getAllAnimals = (animals: IFilteredAnimalList[] | null) => {
        return animals !== null
            ? (animals.find((_) => _.FilterType === AnimalFilterTypeEnum.All)
                ?.Animals as IAnimalSummaryExtraInfo[])
            : null;
    };

    // Farm data can be null if the API doesn't work. Decided to apply timer to check against modifiable value (at the time of writing 15 seconds) when to show view loader.
    // If timer reaches 0 then render the actual component. I believe this is better than showing loading icon continually even if the API doesn't work.
    if (farmData === null && viewLoaderTimer && viewLoaderTimer > 0) return <ViewLoader />;

    const mapUpdatedSalesTypeToAnnouncementData = (response: IFarmAnimal[]) => {
        if (!userSelectedAnimalAnnouncementData) return;

        const updatedAnnouncementData = userSelectedAnimalAnnouncementData.map((announcement) => ({
            ...announcement,
            Batches: announcement.Batches.map((batch) => ({
                ...batch,
                Animals: batch.Animals.map((animal) => {
                    const changedAnimal = response.find(
                        (changed) => changed.AnimalGUID === animal.AnimalId,
                    );
                    return changedAnimal
                        ? { ...animal, SalesType: changedAnimal.SalesType }
                        : animal;
                }),
            })),
        }));

        setUserSelectedAnimalAnnouncementData(updatedAnnouncementData);
    };

    const mapAnnouncementAnimals = () => {
        if (selectedAnimalAnnouncement === null)
            return [];

        return mapAnimalAnnouncementAnimalsToIAnimalSummaryExtraInfo([selectedAnimalAnnouncement], [], farmAnimalGroups);
    }

    const handleShowSnackbarGeneralError = () => {
        enqueueSnackbar(t('AnelmaGeneral:1020'), {
            variant: 'error',
        });
    };

    return (
        <Container id={AnimalAnnouncementsListViewStrings.Container}>
            <ViewTitle>{t('AnelmaLayout:1078')}</ViewTitle>
            <ControlButtons
                handleSelectedAnimalAnnouncementTypeChange={(selectedAnimalAnnouncementType) =>
                    setSelectedAnimalAnnouncementType(selectedAnimalAnnouncementType)
                }
                handleSelectedAnimalAnnouncementStatusChange={(selectedAnimalAnnouncementStatus) =>
                    setSelectedAnimalAnnouncementStatus(selectedAnimalAnnouncementStatus)
                }
                selectedAnimalAnnouncementType={selectedAnimalAnnouncementType}
                selectedAnimalAnnouncementStatus={selectedAnimalAnnouncementStatus}
            />
            <SearchCalendar
                handleSort={() => sortAnimalAnnouncementsBySelectedDates()}
                handleFromDateChange={(fromDate) => setFromDate(fromDate)}
                handleToDateChange={(toDate) => setToDate(toDate)}
                fromDate={fromDate}
                toDate={toDate}
                displayAll={displayAll}
                setDisplayAll={setDisplayAll}
                className={AnimalAnnouncementsListViewStrings.Refiner}
                childrenText={t('AnelmaBovine:1033')}
                fromDateId={AnimalAnnouncementsListViewStrings.RefinerFromDate}
                toDateId={AnimalAnnouncementsListViewStrings.RefinerToDate}
            />
            <AnimalAnnouncementListViewGrid
                data={userSelectedAnimalAnnouncementData}
                loading={isLoading}
                handleAnimalAnnouncementReload={handleAnnouncementReload}
                handleAnimalAnnouncementDelete={(announcementNumber) => {
                    removeAnimalAnnouncement(announcementNumber);
                }}
                handleAnimalAnnouncementDeleteById={(announcementId) => {
                    removeAnimalAnnouncementById(announcementId);
                }}
                handleAnimalAnnouncementModification={(data) =>
                    handleAnimalAnnouncementDialogVisible(data)
                }
                canCreateAnimalAnnouncement={canCreateAnimalAnnouncement}
                reloadChainInfos={reloadChainInfos}
                setReloadChainInfos={setReloadChainInfos}
                displayAll={displayAll}
            />
            <>
                {animalAnnouncementDialogVisible &&
                    selectedAnimalAnnouncement &&
                    (selectedAnimalAnnouncement.Type === AnnouncementTypeEnum.Slaughter ||
                        selectedAnimalAnnouncement.Type ===
                        AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement) ? (
                    <BovineSlaughterAnnouncement
                        data={selectedAnimalAnnouncement}
                        onClose={() => {
                            setReloadChainInfos(true);
                            handleAnimalAnnouncementDialogClosed();
                        }}
                        mode={DialogModeEnum.Edit}
                        selectedAnimals={null}
                        animals={displayAll ? mapAnnouncementAnimals() : farmAnimals}
                        processAnnouncementSave={(data, type, holdingSiteId) =>
                            handleAnnouncementUpdate(data, type, holdingSiteId)
                        }
                        processAnnouncementDelete={removeAnimalAnnouncementById}
                        mapUpdatedSalesTypeToAllAnimalsData={(data) =>
                            mapUpdatedSalesTypeToAnnouncementData(data)
                        }
                        loading={false}
                        canCreateAnimalAnnouncement={!!canCreateAnimalAnnouncement}
                        readOnly={isAnimalAnnouncementUpdateRestricted(selectedAnimalAnnouncement)}
                        announcementConfirmation={
                            selectedAnimalAnnouncement.Type ===
                            AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
                        }
                    />
                ) : animalAnnouncementDialogVisible &&
                    selectedAnimalAnnouncement &&
                    (selectedAnimalAnnouncement.Type === AnnouncementTypeEnum.Intermediation ||
                        selectedAnimalAnnouncement.Type ===
                        AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement) ? (
                    <BovineIntermediationAnnouncement
                        data={selectedAnimalAnnouncement}
                        onClose={handleAnimalAnnouncementDialogClosed}
                        mode={DialogModeEnum.Edit}
                        selectedAnimals={null}
                        animals={displayAll ? mapAnnouncementAnimals() : farmAnimals}
                        processAnnouncementSave={(data, type, holdingSiteId) =>
                            handleAnnouncementUpdate(data, type, holdingSiteId)
                        }
                        processAnnouncementDelete={removeAnimalAnnouncementById}
                        mapUpdatedSalesTypeToAllAnimalsData={(data) =>
                            mapUpdatedSalesTypeToAnnouncementData(data)
                        }
                        loading={false}
                        canCreateAnimalAnnouncement={!!canCreateAnimalAnnouncement}
                        readOnly={isAnimalAnnouncementUpdateRestricted(selectedAnimalAnnouncement)}
                        announcementConfirmation={
                            selectedAnimalAnnouncement.Type ===
                            AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
                        }
                    />
                ) : animalAnnouncementDialogVisible &&
                    selectedAnimalAnnouncement &&
                    (selectedAnimalAnnouncement.Type === AnnouncementTypeEnum.BovinePurchase ||
                        selectedAnimalAnnouncement.Type ===
                        AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement) ? (
                    <BovinePurchaseAnnouncement
                        data={selectedAnimalAnnouncement}
                        onClose={handleAnimalAnnouncementDialogClosed}
                        mode={DialogModeEnum.Edit}
                        processAnnouncementSave={(data, type, holdingSiteId) =>
                            handleAnnouncementUpdate(data, type, holdingSiteId)
                        }
                        processAnnouncementDelete={removeAnimalAnnouncementById}
                        announcementConfirmation={
                            selectedAnimalAnnouncement.Type ===
                            AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
                        }
                        weekReadonly={
                            selectedAnimalAnnouncement.Type ===
                            AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
                        }
                        readonly={isAnimalAnnouncementUpdateRestricted(selectedAnimalAnnouncement)}
                    />
                ) : animalAnnouncementDialogVisible &&
                    selectedAnimalAnnouncement &&
                    selectedAnimalAnnouncement.Type === AnnouncementTypeEnum.ManualBovineSlaughter ? (
                    <ManualAnnouncementDialog
                        announcementData={selectedAnimalAnnouncement}
                        announcementType={AnnouncementTypeEnum.ManualBovineSlaughter}
                        onClose={handleAnimalAnnouncementDialogClosed}
                        mode={DialogModeEnum.Edit}
                        processAnnouncementSave={(data, type, holdingSiteId) =>
                            handleAnnouncementUpdate(data, type, holdingSiteId)
                        }
                        mapUpdatedSalesTypeToAllAnimalsData={(data) => false}
                        animals={[]}
                        withChainInformation={false}
                        readOnly={isAnimalAnnouncementUpdateRestricted(selectedAnimalAnnouncement)}
                    />
                ) : animalAnnouncementDialogVisible &&
                    selectedAnimalAnnouncement &&
                    selectedAnimalAnnouncement.Type ===
                    AnnouncementTypeEnum.ManualBovineIntermediation ? (
                    <ManualAnnouncementDialog
                        announcementData={selectedAnimalAnnouncement}
                        announcementType={AnnouncementTypeEnum.ManualBovineIntermediation}
                        onClose={handleAnimalAnnouncementDialogClosed}
                        mode={DialogModeEnum.Edit}
                        processAnnouncementSave={(data, type, holdingSiteId) =>
                            handleAnnouncementUpdate(data, type, holdingSiteId)
                        }
                        mapUpdatedSalesTypeToAllAnimalsData={(data) => false}
                        animals={[]}
                        withChainInformation={false}
                        readOnly={isAnimalAnnouncementUpdateRestricted(selectedAnimalAnnouncement)}
                    />
                ) : null}
            </>


            {confirmBovinePurchaseAnnouncement.openDialog && (
                <ConfirmBovinePurchaseAnnouncement
                    announcement={confirmBovinePurchaseAnnouncement.announcement}
                    onClose={() => setConfirmBovinePurchaseAnnouncement({ announcement: null, openDialog: false })}
                    processAnnouncementSave={handleAnnouncementUpdate}
                    processAnnouncementDelete={removeAnimalAnnouncementById}
                />
            )}
        </Container>
    );
}
